import React, { useContext, useEffect, useState, useCallback  } from 'react';
import SummaryApi from '../common';
import Context from '../context';
import displayINRCurrency from '../helpers/displayCurrency';
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const Cart = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const context = useContext(Context);
  const isGuest = !context?.user?._id;

  const loadingCart = new Array(Math.max(1, context.cartProductCount || 1)).fill(null);

  const parseCartResponse = (resData) => {
    if (!resData) return [];
    if (Array.isArray(resData)) return resData;
    if (resData.data && Array.isArray(resData.data)) return resData.data;
    if (resData.data?.items && Array.isArray(resData.data.items)) return resData.data.items;
    if (Array.isArray(resData.items)) return resData.items;
    if (Array.isArray(resData.cart)) return resData.cart;
    return [];
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await axios({
        url: SummaryApi.addToCartProductView.url,
        method: SummaryApi.addToCartProductView.method,
        withCredentials: true,
        validateStatus: () => true
      });

      // -------- LOGGED-IN USER --------
      if (res.status !== 401) {
        setData(parseCartResponse(res.data));
        return;
      }

      // -------- GUEST USER --------
      const guestIds = JSON.parse(localStorage.getItem("guest_cart") || "[]");

      // normalize → always string IDs
      const normalizedIds = guestIds
        .map(x => typeof x === "string" ? x : x?.id)
        .filter(Boolean);

      if (!normalizedIds.length) {
        setData([]);
        return;
      }

      let products = [];

      // backend accepts ONE productId at a time
      for (let pid of normalizedIds) {
        try {
          const r = await axios({
            url: SummaryApi.productDetails.url,
            method: SummaryApi.productDetails.method,
            data: { productId: pid }
          });

          if (r.data?.data) products.push(r.data.data);

        } catch (err) {
          //console.error("Guest load failed", pid, err);
        }
      }

      const guestCartMapped = products.map(p => ({
        _id: p._id,
        quantity: 1,
        productId: p
      }));

      setData(guestCartMapped);

    } catch (err) {
      //console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, context?.cartProductCount]);

  const refreshHeaderCart = async () => {
    if (context?.fetchUserAddToCart) await context.fetchUserAddToCart();
  };

  const goToCheckout = () => {
    const checkoutCart = data.map(item => {
      const prod = item.productId || {};
      return {
        id: prod._id,
        name: prod.productName,
        price: prod.sellingPrice,
        qty: item.quantity
      };
    });

    localStorage.setItem("cart", JSON.stringify(checkoutCart));
    localStorage.setItem("totalAmount", totalPrice);
    navigate("/checkout");
  };

  const increaseQty = async (id, qty, stock, productId) => {
    if (isGuest){
      setData(prev =>
        prev.map(item =>
          (item.productId?._id === productId)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    return;} 

    if (qty >= stock) return toast.error(`Only ${stock} item(s) in stock`);

    const res = await axios({
      url: SummaryApi.updateCartProduct.url,
      method: SummaryApi.updateCartProduct.method,
      withCredentials: true,
      data: { _id: id, quantity: qty + 1 }
    });

    if (res.data?.success) {
      fetchData();
      refreshHeaderCart();
    }
  };

  const decreaseQty = async (id, qty, productId) => {
    if (isGuest) {
      if (qty <= 1) return;
        setData(prev =>
          prev.map(item =>
            (item.productId?._id === productId)
              ? { ...item, quantity: item.quantity - 1 }
              : item
          )
        );
      return;}
    

    const res = await axios({
      url: SummaryApi.updateCartProduct.url,
      method: SummaryApi.updateCartProduct.method,
      withCredentials: true,
      data: { _id: id, quantity: qty - 1 }
    });

    if (res.data?.success) {
      fetchData();
      refreshHeaderCart();
    }
  };

  const deleteCartProduct = async (productId) => {
    if (isGuest) {
        let cart = JSON.parse(localStorage.getItem("guest_cart") || "[]");
        cart = cart
      .map(x => typeof x === "string" ? x : x?.id)
      .filter(id => id && id !== productId);

    localStorage.setItem("guest_cart", JSON.stringify(cart));

    // update UI immediately
    setData(prev =>
      prev.filter(item =>
        item.productId?._id !== productId &&
        item._id !== productId
      )
    );
    window.dispatchEvent(new Event("guest_cart_changed"));

    return;    }

    await axios({
      url: SummaryApi.deleteCartProduct.url,
      method: SummaryApi.deleteCartProduct.method,
      withCredentials: true,
      data: { _id: productId }
    });

    fetchData();
    refreshHeaderCart();
  };

  const totalQty = data.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalPrice = data.reduce(
    (s, i) => s + (i.quantity * (i.productId?.sellingPrice || 0)),
    0
  );

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-10 py-6">
      <div className="flex flex-col lg:flex-row gap-6 justify-center">

        <div className="w-full lg:max-w-3xl">
          {loading ? (
            loadingCart.map((_, i) => (
              <div key={i} className="h-32 animate-pulse my-3 rounded" />
            ))
          ) : data.length === 0 ? (
            <p className="bg-white text-center py-6 rounded">
              Your cart is empty
            </p>
          ) : (
            data.map((item) => {
              const prod = item.productId || {};
              const stock = prod.stock ?? 0;

              return (
                <div key={item._id} className="flex flex-col sm:flex-row bg-white my-4 border rounded overflow-hidden">

                  <div className="w-full sm:w-32 h-32 flex justify-center items-center">
                    <img src={prod.productImage?.[0]} className="h-full object-scale-down" alt="" />
                  </div>

                  <div className="flex-1 p-4 relative">
                    <MdDelete
                      className="absolute right-3 top-3 cursor-pointer text[#5a3620]"
                      onClick={() => deleteCartProduct(item.productId?._id || item._id)}
                    />

                    <h2 className="font-medium">{prod.productName}</h2>
                    <p className="text-slate-500 text-sm">{prod.category}</p>

                    <div className="flex justify-between mt-2">
                      <span className="text-green-600 font-medium">
                        {displayINRCurrency(prod.sellingPrice)}
                      </span>
                      <span>
                        {displayINRCurrency(prod.sellingPrice * item.quantity)}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={() => decreaseQty(item._id, item.quantity, prod._id)}
                        className="border w-7 h-7 rounded text-lg"
                      >−</button>

                      <span>{item.quantity}</span>

                      <button
                        onClick={() => increaseQty(item._id, item.quantity, stock, prod._id)}
                        className="border w-7 h-7 rounded text-lg"
                      >+</button>

                      <span className="text-xs text-slate-500">Stock: {stock}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="w-full lg:max-w-sm mt-4">
          <div className="bg-white border rounded sticky top-24">
            <h2 className="bg-[#5a3620] text-white px-4 py-2 rounded-t">Order Summary</h2>

            <div className="flex justify-between px-4 py-2">
              <span>Quantity</span>
              <span>{totalQty}</span>
            </div>

            <div className="flex justify-between px-4 py-2 font-semibold">
              <span>Total</span>
              <span>{displayINRCurrency(totalPrice)}</span>
            </div>

            <button
              onClick={goToCheckout}
              className="bg-[#5a3620] text-white w-full py-3 rounded-b"
            >
              Proceed to Payment
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Cart;
